import React from 'react';
import type { Log } from '../../types/log';
import ClinicLogDetails from './ClinicLogDetails';
import { generatePlaybackUrl, navigateToPlayback } from '../../services/playbackUrl';

interface LogListProps {
  logs: Log[];
  onSelectLog: (logId: string | null) => void;
  onEditLog: (log: Log) => void;
}

const LogList: React.FC<LogListProps> = ({ logs, onSelectLog, onEditLog }) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'archived':
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      case 'cancelled':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    }
  };

  const formatTimestamp = (timestamp: number | undefined) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <svg
          className="h-16 w-16 dark:text-claude-darkTextSecondary text-claude-textSecondary mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
        <h3 className="text-lg font-medium dark:text-claude-darkText text-claude-text mb-2">
          No logs yet
        </h3>
        <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary text-center">
          Create a new log to get started
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-auto">
      <div className="space-y-2">
        {logs.map((log) => (
          <div
            key={log.id}
            className={`
              p-4 rounded-lg border transition-colors cursor-pointer
              dark:border-claude-darkBorder border-claude-border
              hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover
              ${log.status === 'completed' ? 'opacity-60' : ''}
            `}
            onClick={() => onSelectLog(log.id)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      log.priority >= 3
                        ? 'bg-red-500'
                        : log.priority >= 2
                        ? 'bg-yellow-500'
                        : log.priority >= 1
                        ? 'bg-green-500'
                        : 'bg-gray-500'
                    }`}
                  />
                  <h3 className={`text-sm font-medium dark:text-claude-darkText text-claude-text truncate ${log.status === 'completed' ? 'line-through' : ''}`}>
                    {log.title}
                  </h3>
                </div>

                {log.description && log.category !== 'clinic_clinical_log' && (
                  <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary line-clamp-2 mb-2 whitespace-pre-wrap">
                    {log.description}
                  </p>
                )}

                {log.category === 'clinic_clinical_log' && (
                  <ClinicLogDetails log={log} compact />
                )}

                <div className="flex items-center gap-3 text-xs">
                  <span className={`px-2 py-0.5 rounded-full border ${getStatusBadge(log.status)}`}>
                    {log.status}
                  </span>
                  <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary capitalize">
                    {log.category}
                  </span>
                  {log.startTime && (
                    <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary">
                      Start: {formatTimestamp(log.startTime)}
                    </span>
                  )}
                  {log.endTime && (
                    <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary">
                      End: {formatTimestamp(log.endTime)}
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditLog(log);
                }}
                className="p-1.5 rounded-lg hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
              >
                <svg
                  className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
              {log.startTime && generatePlaybackUrl(log) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToPlayback(log);
                  }}
                  className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  title="View in Dashboard Playback"
                >
                  <svg className="h-4 w-4 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LogList;